import { useEffect, useMemo, useState } from 'react';
import { CloudPolicy } from '../../types/policies';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../../ui/common-ui/dialog';
import { Button } from '../../../../ui/common-ui/button';
import { Input } from '../../../../ui/common-ui/input';
import { Textarea } from '../../../../ui/common-ui/textarea';
import { Label } from '../../../../ui/common-ui/label';
import { Badge } from '../../../../ui/common-ui/badge';
import { ScrollArea } from '../../../../ui/common-ui/scroll-area';
import {
  Activity,
  AlertCircle,
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronRight,
  Clock,
  Cloud,

  Database,
  Flame,
  Info,
  KeyRound,
  Link2,
  Loader2,
  Search,
  ShieldCheck,
  ShieldOff,
  Sparkles,
  User,
  Wrench,
  X,
} from 'lucide-react';
import { updatePolicy } from '../../services/api';
import { toast } from 'sonner';
import { normalizeCloudPolicy, normalizePolicySeverity } from '../../lib/policyTransforms';
import { filterResourcesByPolicyTargetTypes } from '../../lib/resourceTargeting';
import {
  fetchAlertSelectableItems,
  fetchAlerts,
  SelectableItemsResponse,
} from '../../../CloudAlertManagement/services/api';

const formatRelativeTime = (dateStr: string) => {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffDays > 30) return `${Math.floor(diffDays / 30)}mo ago`;
  if (diffDays > 0) return `${diffDays}d ago`;
  if (diffHours > 0) return `${diffHours}h ago`;
  if (diffMins > 0) return `${diffMins}m ago`;
  return 'Just now';
};

interface PolicyDetailsEditProps {
  policy: CloudPolicy | null;
  open: boolean;
  onClose: () => void;
  tenantName?: string;
  cloudId?: string;
  isCreateMode?: boolean;
  onPolicyUpdate?: (policy: CloudPolicy) => void;
  linkedAlertDetails?: Record<string, any>;
}

type TargetKind = 'users' | 'nhis' | 'resources';

type PickerItem = {
  id: string;
  label: string;
  sublabel?: string;
};

type AlertOption = {
  id: string;
  title: string;
  severity: string;
  permission: string;
  type?: string;
};

const severityStyles: Record<string, { badge: string; panel: string; dot: string; active: string; icon: any }> = {
  Critical: {
    badge: 'bg-rose-500/10 text-rose-600 border-rose-500/30',
    panel: 'bg-rose-50 text-rose-700 border-rose-200',
    dot: 'bg-rose-500',
    active: 'border-rose-300 bg-rose-50 text-rose-700 shadow-sm',
    icon: Flame,
  },
  High: {
    badge: 'bg-orange-500/10 text-orange-600 border-orange-500/30',
    panel: 'bg-orange-50 text-orange-700 border-orange-200',
    dot: 'bg-orange-500',
    active: 'border-orange-300 bg-orange-50 text-orange-700 shadow-sm',
    icon: AlertTriangle,
  },
  Medium: {
    badge: 'bg-amber-500/10 text-amber-600 border-amber-500/30',
    panel: 'bg-amber-50 text-amber-700 border-amber-200',
    dot: 'bg-amber-500',
    active: 'border-amber-300 bg-amber-50 text-amber-700 shadow-sm',
    icon: AlertCircle,
  },
  Low: {
    badge: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30',
    panel: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    dot: 'bg-emerald-500',
    active: 'border-emerald-300 bg-emerald-50 text-emerald-700 shadow-sm',
    icon: Info,
  },
};

const severityOptions = ['Critical', 'High', 'Medium', 'Low'];

const targetConfig: Record<TargetKind, { title: string; icon: any; accent: string; empty: string }> = {
  users: {
    title: 'Targeted Users',
    icon: User,
    accent: 'text-blue-600 bg-blue-50 border-blue-200',
    empty: 'No users returned for this cloud context.',
  },
  nhis: {
    title: 'Targeted Service Accounts',
    icon: KeyRound,
    accent: 'text-violet-600 bg-violet-50 border-violet-200',
    empty: 'No service accounts, roles, secrets, or tokens returned.',
  },
  resources: {
    title: 'Targeted Resources',
    icon: Database,
    accent: 'text-emerald-600 bg-emerald-50 border-emerald-200',
    empty: 'No resources returned for this cloud context.',
  },
};

const getVisibleTargetKinds = (targetType?: string): TargetKind[] => {
  return ['users', 'nhis', 'resources'];
};

const asStringArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  return value.map(item => String(item)).filter(Boolean);
};

const hasAllTargets = (value: string[]) => value.some(item => item === '' || item === '*');

const toggleValue = (list: string[], value: string) => (
  list.includes(value) ? list.filter(item => item !== value) : [...list, value]
);

const extractPermission = (steps: any): string => {
  const values = steps && typeof steps === 'object' ? Object.values(steps) : [];
  for (const step of values) {
    if (typeof step === 'string') return step;
    if (step && typeof step === 'object') {
      const stepData = step as Record<string, any>;
      return stepData.action || stepData.operation || stepData.eventName || stepData.eventSource || Object.keys(stepData.conditions || {})[0] || 'Permission context unavailable';
    }
  }
  return 'Permission context unavailable';
};

const flattenAlerts = (grouped: any): AlertOption[] => {
  const alerts: AlertOption[] = [];

  Object.entries(grouped || {}).forEach(([type, severityGroups]) => {
    Object.values((severityGroups || {}) as Record<string, any>).forEach((items: any) => {
      if (!Array.isArray(items)) return;
      items.forEach((alert: any) => {
        const id = String(alert.alertId || alert.id || '');
        if (!id) return;
        alerts.push({
          id,
          title: alert.title || 'Untitled alert',
          severity: normalizePolicySeverity(alert.severity),
          permission: extractPermission(alert.steps),
          type,
        });
      });
    });
  });

  return alerts;
};

function SeverityEditor({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="rounded-2xl border border-border/50 bg-muted/20 p-1 shadow-inner">
      <div className="grid grid-cols-2 gap-1 sm:grid-cols-4">
        {severityOptions.map(option => {
          const style = severityStyles[option] || severityStyles.Medium;
          const Icon = style.icon;
          const selected = normalizePolicySeverity(value) === option;

          return (
            <button
              key={option}
              type="button"
              onClick={() => onChange(option)}
              className={`inline-flex h-8 items-center justify-center gap-1.5 rounded-md border px-2 text-xs font-semibold transition ${
                selected
                  ? style.active
                  : 'border-transparent bg-transparent text-slate-500 hover:border-slate-200 hover:bg-white hover:text-slate-800'
              }`}
            >
              {selected ? (
                <Check className="h-3.5 w-3.5" />
              ) : (
                <Icon className={`h-3.5 w-3.5 ${style.badge.split(' ').slice(1, 2).join(' ')}`} />
              )}
              {option}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function TargetPicker({
  kind,
  items,
  included,
  excluded,
  onIncludedChange,
  onExcludedChange,
}: {
  kind: TargetKind;
  items: PickerItem[];
  included: string[];
  excluded: string[];
  onIncludedChange: (next: string[]) => void;
  onExcludedChange: (next: string[]) => void;
}) {
  const [query, setQuery] = useState('');
  const config = targetConfig[kind];
  const Icon = config.icon;

  const filtered = items.filter(item => {
    const searchText = `${item.label} ${item.sublabel || ''}`.toLowerCase();
    return searchText.includes(query.toLowerCase());
  });
  const allIncluded = hasAllTargets(included);
  const allExcluded = hasAllTargets(excluded);

  const allItemIds = items.map(item => item.id);
  const hasItems = allItemIds.length > 0;

  const isEveryItemIncluded = allIncluded && excluded.length === 0;
  const isEveryItemExcluded = allExcluded && included.length === 0;

  const includedCount = allIncluded
    ? items.length - items.filter(item => excluded.includes(item.id)).length
    : included.filter(id => id !== '*' && id !== '').length;

  const excludedCount = allExcluded
    ? items.length - items.filter(item => included.includes(item.id)).length
    : excluded.filter(id => id !== '*' && id !== '').length;

  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <section className="flex flex-col rounded-2xl border border-border/50 bg-card p-4 shadow-lg shadow-sky-500/5 transition-all duration-300 hover:border-primary/30 hover:shadow-[0_4px_16px_hsl(var(--primary)/0.06)]">
      <div 
        className="flex cursor-pointer select-none flex-col gap-3 rounded-md sm:flex-row sm:items-start sm:justify-between"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-2">
          <span className={`inline-flex h-8 w-8 items-center justify-center rounded-md border ${config.accent}`}>
            <Icon className="h-4 w-4" />
          </span>
          <div>
            <h3 className="text-sm font-semibold text-slate-900">{config.title}</h3>
            <p className="text-xs text-slate-500">{includedCount} included, {excludedCount} excluded</p>
          </div>
        </div>
        <div className="flex w-full flex-wrap items-center gap-2 pl-10 sm:w-auto sm:pl-0">
          <Button
            type="button"
            size="sm"
            variant={isEveryItemIncluded ? 'default' : 'outline'}
            disabled={!hasItems}
            className="h-8 rounded-md px-2.5 text-xs"
            onClick={(e) => {
              e.stopPropagation();
              onIncludedChange(allItemIds);
              onExcludedChange([]);
            }}
          >
            {isEveryItemIncluded ? <Check className="mr-1 h-3 w-3" /> : null}
            Include all
          </Button>
          <Button
            type="button"
            size="sm"
            variant={isEveryItemExcluded ? 'destructive' : 'outline'}
            disabled={!hasItems}
            className="h-8 rounded-md px-2.5 text-xs"
            onClick={(e) => {
              e.stopPropagation();
              onIncludedChange([]);
              onExcludedChange(allItemIds);
            }}
          >
            {isEveryItemExcluded ? <Check className="mr-1 h-3 w-3" /> : null}
            Exclude all
          </Button>
          <div className={`ml-auto p-1 text-slate-400 transition-transform sm:ml-0 ${isExpanded ? 'rotate-180' : ''}`}>
            <ChevronDown className="h-4 w-4" />
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="mt-4 flex min-h-[300px] max-h-[400px] flex-col">
          <div className="relative mb-3 shrink-0">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              value={query}
              onChange={event => setQuery(event.target.value)}
              placeholder={`Search ${config.title.toLowerCase()}...`}
              className="h-10 rounded-md border-slate-200 bg-slate-50 pl-9"
              onClick={e => e.stopPropagation()}
            />
          </div>

          <div className="min-h-0 flex-1 space-y-2 overflow-y-auto pb-2 pr-1">
            {filtered.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-border/50 bg-muted/20 p-4 text-sm text-slate-500">
                {config.empty}
              </div>
            ) : (
              filtered.map(item => {
                const isExplicitlyExcluded = excluded.includes(item.id);
                const isExplicitlyIncluded = included.includes(item.id);
              
                const isExcluded = allExcluded ? !isExplicitlyIncluded : isExplicitlyExcluded;
                const isIncluded = allIncluded ? !isExplicitlyExcluded : isExplicitlyIncluded;
                const toggleIncluded = () => {
                  if (allIncluded) {
                    if (isIncluded) onExcludedChange([...excluded, item.id]);
                    else onExcludedChange(excluded.filter(id => id !== item.id));
                  } else if (allExcluded) {
                    if (isIncluded) onIncludedChange(included.filter(id => id !== item.id));
                    else onIncludedChange([...included, item.id]);
                  } else {
                    onIncludedChange(toggleValue(included, item.id));
                    if (!isIncluded && isExcluded) onExcludedChange(excluded.filter(value => value !== item.id));
                  }
                };
                const toggleExcluded = () => {
                  if (allIncluded) {
                    if (isExcluded) onExcludedChange(excluded.filter(id => id !== item.id));
                    else onExcludedChange([...excluded, item.id]);
                  } else if (allExcluded) {
                    if (isExcluded) onIncludedChange([...included, item.id]);
                    else onIncludedChange(included.filter(id => id !== item.id));
                  } else {
                    onExcludedChange(toggleValue(excluded, item.id));
                    if (!isExcluded && isIncluded) onIncludedChange(included.filter(value => value !== item.id));
                  }
                };

                return (
                  <div
                    key={item.id}
                    className={`grid grid-cols-[auto_minmax(0,1fr)] items-center gap-3 rounded-2xl border px-3 py-2 shadow-sm transition-all duration-300 hover:border-primary/30 hover:shadow-[0_4px_16px_hsl(var(--primary)/0.06)] sm:grid-cols-[auto_minmax(0,1fr)_auto] ${
                      isExcluded
                        ? 'border-rose-300 bg-rose-50/70'
                        : isIncluded
                        ? 'border-primary/40 bg-primary/5'
                        : 'border-border/50 bg-card'
                    }`}
                  >
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleIncluded();
                      }}
                      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition-colors ${
                        isIncluded
                          ? 'border-primary bg-primary text-primary-foreground shadow-sm'
                          : 'border-slate-300 bg-white text-transparent hover:border-primary/50'
                      }`}
                      aria-label={isIncluded ? `Unselect ${item.label}` : `Select ${item.label}`}
                    >
                      <Check className="h-3.5 w-3.5" />
                    </button>

                    <div className="min-w-0">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleIncluded();
                        }}
                        className="block w-full min-w-0 text-left"
                      >
                        <p className="truncate text-sm font-medium text-slate-800">{item.label}</p>
                        {item.sublabel && <p className="truncate text-xs text-slate-500">{item.sublabel}</p>}
                      </button>
                    </div>
                    <div className="col-span-2 flex min-w-0 flex-wrap items-center justify-end gap-1.5 sm:col-span-1 sm:flex-nowrap">
                      <Button
                        type="button"
                        size="sm"
                        variant={isIncluded ? 'default' : 'outline'}
                        className="h-7 w-7 shrink-0 rounded-md p-0"
                        title={isIncluded ? 'Remove include' : 'Include'}
                        aria-label={isIncluded ? `Remove ${item.label} from included targets` : `Include ${item.label}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleIncluded();
                        }}
                      >
                        <Check className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant={isExcluded ? 'destructive' : 'outline'}
                        className="h-7 w-7 shrink-0 rounded-md p-0"
                        title={isExcluded ? 'Remove exclude' : 'Exclude'}
                        aria-label={isExcluded ? `Remove ${item.label} from excluded targets` : `Exclude ${item.label}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleExcluded();
                        }}
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </section>
  );
}

export function PolicyDetailsEdit({
  policy,
  open,
  onClose,
  tenantName,
  cloudId,
  isCreateMode,
  onPolicyUpdate,
  linkedAlertDetails
}: PolicyDetailsEditProps) {
  const [editedPolicy, setEditedPolicy] = useState<CloudPolicy | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [selectableItems, setSelectableItems] = useState<SelectableItemsResponse | null>(null);
  const [isLoadingSelectable, setIsLoadingSelectable] = useState(false);
  const [availableAlerts, setAvailableAlerts] = useState<AlertOption[]>([]);
  const [isLoadingAlerts, setIsLoadingAlerts] = useState(false);
  const [alertQuery, setAlertQuery] = useState('');
  const [activeTab, setActiveTab] = useState<'details' | 'timeline' | 'data'>('details');

  useEffect(() => {
    if (policy) setEditedPolicy(normalizeCloudPolicy(policy));
  }, [policy]);

  useEffect(() => {
    if (!open || !editedPolicy || !tenantName || !cloudId) return;

    setIsLoadingSelectable(true);
    const actualCloudId = editedPolicy.cloudAccountId || cloudId;
    fetchAlertSelectableItems(editedPolicy.tenantName || tenantName, actualCloudId, editedPolicy.cloudType)
      .then(setSelectableItems)
      .catch(error => {
        console.error('Failed to fetch policy selectable items:', error);
        setSelectableItems({ users: [], resources: {} });
      })
      .finally(() => setIsLoadingSelectable(false));
  }, [open, editedPolicy?.cloudType, tenantName, cloudId]);

  useEffect(() => {
    if (!open || !editedPolicy || editedPolicy.cloudPolicyType !== 'REMEDIATION' || !tenantName || !cloudId) return;

    setIsLoadingAlerts(true);
    const actualCloudId = editedPolicy.cloudAccountId || cloudId;
    fetchAlerts(editedPolicy.tenantName || tenantName, actualCloudId, editedPolicy.cloudType)
      .then(response => setAvailableAlerts(flattenAlerts(response)))
      .catch(error => {
        console.error('Failed to fetch alerts for policy linking:', error);
        setAvailableAlerts([]);
      })
      .finally(() => setIsLoadingAlerts(false));
  }, [open, editedPolicy?.cloudType, editedPolicy?.cloudPolicyType, tenantName, cloudId]);

  const linkedAlertMap = useMemo(() => {
    const map: Record<string, AlertOption> = {};

    availableAlerts.forEach(alert => {
      map[alert.id] = alert;
    });

    Object.entries(linkedAlertDetails || {}).forEach(([id, alert]: [string, any]) => {
      map[id] = {
        id,
        title: alert.title || id,
        severity: normalizePolicySeverity(alert.severity),
        permission: extractPermission(alert.steps),
        type: alert.alertType,
      };
    });

    return map;
  }, [availableAlerts, linkedAlertDetails]);

  const targetItems = useMemo(() => {
    const users = (selectableItems?.users || []).map(user => ({ id: user, label: user }));
    const nhis = (
      editedPolicy?.cloudType === 'AWS'
        ? selectableItems?.awsIamRole || []
        : selectableItems?.serviceAccount || selectableItems?.serviceAccounts || []
    ).map(item => ({ id: item, label: item }));

    const policyResources = filterResourcesByPolicyTargetTypes(
      selectableItems?.resources,
      editedPolicy?.policyTargetTypes,
      editedPolicy?.cloudType,
    );

    const resources = Object.entries(policyResources).flatMap(([group, values]) => (
      (values || []).map(value => ({
        id: value,
        label: value,
        sublabel: group.replace(/-/g, ' '),
      }))
    ));

    return { users, nhis, resources };
  }, [selectableItems, editedPolicy?.cloudType, editedPolicy?.policyTargetTypes]);

  if (!editedPolicy) return null;

  const isRemediation = editedPolicy.cloudPolicyType === 'REMEDIATION';
  const visibleTargets = getVisibleTargetKinds(editedPolicy.policyTargetTypes?.join(','));
  const severity = normalizePolicySeverity(editedPolicy.severity);
  const severityStyle = severityStyles[severity] || severityStyles.Medium;
  const metadata = editedPolicy.policyMetadata && typeof editedPolicy.policyMetadata === 'object' ? editedPolicy.policyMetadata : {};
  const excludedUsers = asStringArray(editedPolicy.excludedUsers);
  const excludedResources = asStringArray(editedPolicy.excludedResources);
  const excludedNHIs = asStringArray(editedPolicy.excludedNHIs);
  const linkedAlertIds = asStringArray(editedPolicy.linkedAlerts);
  const filteredAlerts = availableAlerts.filter(alert => (
    `${alert.title} ${alert.severity} ${alert.permission}`.toLowerCase().includes(alertQuery.toLowerCase())
  ));

  const setPolicyList = (key: 'includedUsers' | 'includedResources' | 'includedNHIs' | 'excludedUsers' | 'excludedResources' | 'excludedNHIs', value: string[]) => {
    setEditedPolicy(prev => ({ ...prev!, [key]: value }));
  };

  const toggleLinkedAlert = (alertId: string) => {
    setEditedPolicy(prev => ({
      ...prev!,
      linkedAlerts: toggleValue(asStringArray(prev?.linkedAlerts), alertId),
    }));
  };

  const handleSave = async () => {
    if (!editedPolicy.title.trim()) {
      toast.error('Policy title is required');
      return;
    }

    const cleanPolicy = normalizeCloudPolicy({
      ...editedPolicy,
      severity,
      linkedAlerts: isRemediation ? linkedAlertIds : [],
    });

    if (isCreateMode && onPolicyUpdate) {
      onPolicyUpdate(cleanPolicy);
      onClose();
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        ...cleanPolicy,
        policyId: cleanPolicy.policyId || cleanPolicy.id?.toString(),
        tenantName: cleanPolicy.tenantName || tenantName,
        cloudId: cleanPolicy.cloudAccountId || cloudId,
      };

      const updated = await updatePolicy(payload);
      toast.success('Policy updated successfully');
      onPolicyUpdate?.(normalizeCloudPolicy(updated));
      onClose();
    } catch (error) {
      console.error('Failed to update policy', error);
      toast.error('Failed to update policy');
    } finally {
      setIsSaving(false);
    }
  };

  const executions = Array.isArray(editedPolicy.executionHistory) ? editedPolicy.executionHistory : [];

  return (
    <Dialog open={open} onOpenChange={(val) => !val && onClose()}>
      <DialogContent className="flex h-[92vh] w-[calc(100vw-2rem)] max-w-6xl flex-col overflow-hidden rounded-2xl border-border/70 bg-background p-0 shadow-lg shadow-sky-500/5">
        <DialogHeader className="border-b border-border/50 bg-card px-6 py-5">
          <div className="flex items-center justify-center">
            <div className="flex flex-wrap items-center justify-center gap-2">
              <Badge variant="outline" className="rounded-md bg-sky-50 text-sky-700 border-sky-200">
                  <Cloud className="mr-1 h-3 w-3" />
                  {editedPolicy.cloudType}
                </Badge>
                <Badge variant="outline" className={severityStyle.badge}>
                  <span className={`mr-1.5 h-1.5 w-1.5 rounded-full ${severityStyle.dot}`} />
                  {severity}
                </Badge>
                <Badge variant="outline" className={isRemediation ? 'rounded-md bg-violet-50 text-violet-700 border-violet-200' : 'rounded-md bg-rose-50 text-rose-700 border-rose-200'}>
                  {isRemediation ? <Wrench className="mr-1 h-3 w-3" /> : <ShieldCheck className="mr-1 h-3 w-3" />}
                  {isRemediation ? 'Remediation' : 'Guardrail'}
                </Badge>
                {editedPolicy.policyTargetTypes && editedPolicy.policyTargetTypes.length > 0 && editedPolicy.policyTargetTypes.map((type, idx) => (
                  <Badge key={idx} variant="outline" className="rounded-md bg-slate-50 text-slate-700 border-slate-200">
                    {type}
                  </Badge>
                ))}
            </div>
            <DialogTitle className="hidden">Policy Details</DialogTitle>
          </div>
        </DialogHeader>

        {/* ─── Tab Bar ─────────────────────────────────────────────── */}
        <div className="flex items-center justify-center gap-1 border-b border-border/50 bg-card px-6">
          {[
            { key: 'details' as const, label: 'Policy Details', icon: Sparkles },
            { key: 'timeline' as const, label: 'Execution History', icon: Activity },
            { key: 'data' as const, label: 'Policy Data', icon: ShieldCheck },
          ].map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={`relative flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${
                  isActive
                    ? 'text-primary'
                    : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
                {isActive && (
                  <span className="absolute bottom-0 left-0 right-0 h-0.5 rounded-full bg-primary" />
                )}
              </button>
            );
          })}
        </div>

        <ScrollArea className="min-h-0 flex-1 overflow-x-hidden">
          <div className="box-border w-full max-w-full overflow-x-hidden p-6 pb-8">

            {/* ─── TAB: Policy Details ─────────────────────────────── */}
            {activeTab === 'details' && (
            <div className="w-full max-w-full space-y-5 overflow-hidden">

              {/* Policy Definition */}
              <section className="shrink-0 rounded-2xl border border-border/50 bg-card p-5 shadow-lg shadow-sky-500/5 transition-all duration-300 hover:border-primary/30 hover:shadow-[0_4px_16px_hsl(var(--primary)/0.06)]">
                <div className="mb-4 flex flex-col gap-3">
                  <div className="flex items-center gap-2">
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-primary/10 text-primary">
                      <Sparkles className="h-4 w-4" />
                    </span>
                    <div>
                      <h3 className="text-sm font-semibold text-slate-900">Policy Definition</h3>
                      <p className="text-xs text-slate-500">Edit the visible policy metadata before saving.</p>
                    </div>
                  </div>
                  <div className="pl-11">
                    <SeverityEditor
                      value={severity}
                      onChange={(value: string) => setEditedPolicy(prev => ({ ...prev!, severity: normalizePolicySeverity(value) }))}
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Title</Label>
                    <Input
                      value={editedPolicy.title || ''}
                      onChange={event => setEditedPolicy(prev => ({ ...prev!, title: event.target.value }))}
                      className="h-11 rounded-md border-slate-200 bg-white text-base font-medium"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea
                      value={editedPolicy.description || ''}
                      onChange={event => setEditedPolicy(prev => ({ ...prev!, description: event.target.value }))}
                      className="min-h-28 resize-none rounded-md border-slate-200 bg-white leading-6"
                    />
                  </div>
                </div>
              </section>

              {/* Linked Alerts (Remediation only) */}
              {isRemediation && (
                <section className="w-full max-w-full overflow-hidden rounded-2xl border border-border/50 bg-card p-4 shadow-lg shadow-sky-500/5 transition-all duration-300 hover:border-primary/30 hover:shadow-[0_4px_16px_hsl(var(--primary)/0.06)]">
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <span className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-violet-200 bg-violet-50 text-violet-600">
                        <Link2 className="h-4 w-4" />
                      </span>
                      <div>
                        <h3 className="text-sm font-semibold text-slate-900">Linked Alerts</h3>
                        <p className="text-xs text-slate-500">
                          {isCreateMode ? 'Search alerts from this tenant and cloud account.' : 'Alerts connected to this remediation policy.'}
                        </p>
                      </div>
                    </div>
                    <Badge variant="secondary" className="rounded-md">{linkedAlertIds.length}</Badge>
                  </div>

                  <div className={isCreateMode ? 'mb-3 space-y-2' : 'grid grid-cols-1 gap-3 md:grid-cols-2'}>
                    {linkedAlertIds.length === 0 ? (
                      <div className="rounded-2xl border border-dashed border-border/50 bg-muted/20 p-4 text-sm text-slate-500 md:col-span-2">
                        No alerts linked yet.
                      </div>
                    ) : linkedAlertIds.map(alertId => {
                      const alert = linkedAlertMap[alertId];
                      const alertSeverity = normalizePolicySeverity(alert?.severity);
                      const alertSeverityStyle = severityStyles[alertSeverity] || severityStyles.Medium;
                      return (
                        <div
                          key={alertId}
                          className={`group relative max-w-full overflow-hidden rounded-2xl border border-l-4 ${alertSeverityStyle.panel} bg-card p-3 shadow-lg shadow-sky-500/5 transition-all duration-300 hover:-translate-y-px hover:border-primary/30 hover:border-l-primary hover:shadow-[0_4px_16px_hsl(var(--primary)/0.06)]`}
                        >
                          <div className="flex min-w-0 items-start justify-between gap-3">
                            <div className="min-w-0">
                              <p className="truncate text-sm font-semibold text-slate-800">{alert?.title || alertId}</p>
                              <p className="mt-1 truncate text-xs text-slate-500">{alert?.permission || 'Permission context unavailable'}</p>
                            </div>
                            <div className="flex shrink-0 items-center gap-2">
                              <Badge variant="outline" className={`${alertSeverityStyle.badge} rounded-md text-[10px]`}>
                                {alertSeverity}
                              </Badge>
                              {isCreateMode && (
                                <button
                                  type="button"
                                  onClick={() => toggleLinkedAlert(alertId)}
                                  className="rounded-md p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-600"
                                >
                                  <X className="h-3.5 w-3.5" />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {isCreateMode && (
                    <>
                      <div className="relative mb-3">
                        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                        <Input
                          value={alertQuery}
                          onChange={event => setAlertQuery(event.target.value)}
                          placeholder="Search alerts by title, severity, or permission..."
                          className="h-10 rounded-md border-slate-200 bg-slate-50 pl-9"
                        />
                      </div>

                      <div className="max-h-64 space-y-2 overflow-y-auto pr-1">
                        {isLoadingAlerts ? (
                          <div className="rounded-2xl border border-border/50 bg-muted/20 p-4 text-center text-sm text-slate-500">
                            <Loader2 className="mx-auto mb-2 h-4 w-4 animate-spin text-primary" />
                            Loading alerts...
                          </div>
                        ) : filteredAlerts.length === 0 ? (
                          <div className="rounded-2xl border border-dashed border-border/50 bg-muted/20 p-4 text-sm text-slate-500">
                            No matching alerts found.
                          </div>
                        ) : filteredAlerts.map(alert => {
                          const selected = linkedAlertIds.includes(alert.id);
                          const alertSeverityStyle = severityStyles[normalizePolicySeverity(alert.severity)] || severityStyles.Medium;
                          return (
                            <button
                              type="button"
                              key={alert.id}
                              onClick={() => toggleLinkedAlert(alert.id)}
                              className={`w-full rounded-2xl border p-3 text-left transition-all duration-300 ${
                                selected
                                  ? 'border-primary bg-primary/5'
                                  : 'border-border/50 bg-card hover:border-primary/30 hover:bg-muted/20 hover:shadow-[0_4px_16px_hsl(var(--primary)/0.06)]'
                              }`}
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                  <p className="truncate text-sm font-semibold text-slate-800">{alert.title}</p>
                                  <p className="mt-1 truncate text-xs text-slate-500">{alert.permission}</p>
                                </div>
                                <div className="flex shrink-0 items-center gap-2">
                                  <Badge variant="outline" className={`${alertSeverityStyle.badge} rounded-md text-[10px]`}>
                                    {alert.severity}
                                  </Badge>
                                  <span className={`inline-flex h-5 w-5 items-center justify-center rounded-md border ${
                                    selected ? 'border-primary bg-primary text-white' : 'border-slate-200 bg-white'
                                  }`}>
                                    {selected && <Check className="h-3 w-3" />}
                                  </span>
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}
                </section>
              )}

              {/* Target Pickers */}
              {isLoadingSelectable ? (
                <div className="flex flex-col gap-5">
                  {visibleTargets.map(kind => {
                    const config = targetConfig[kind];
                    const Icon = config.icon;

                    return (
                      <section key={kind} className="flex min-h-[360px] flex-1 flex-col rounded-2xl border border-border/50 bg-card p-4 shadow-lg shadow-sky-500/5">
                        <div className="mb-3 flex items-center gap-2">
                          <span className={`inline-flex h-8 w-8 items-center justify-center rounded-md border ${config.accent}`}>
                            <Icon className="h-4 w-4" />
                          </span>
                          <div>
                            <h3 className="text-sm font-semibold text-slate-900">{config.title}</h3>
                            <p className="text-xs text-slate-500">Loading selectable targets...</p>
                          </div>
                        </div>
                        <div className="flex min-h-0 flex-1 items-center justify-center rounded-2xl border border-dashed border-border/50 bg-muted/20 text-sm text-slate-500">
                          <div className="text-center">
                            <Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin text-primary" />
                            Loading {config.title.toLowerCase()}...
                          </div>
                        </div>
                      </section>
                    );
                  })}
                </div>
              ) : (
                <div className="flex flex-col gap-5">
                  {visibleTargets.map(kind => (
                    <TargetPicker
                      key={kind}
                      kind={kind}
                      items={targetItems[kind]}
                      included={
                        kind === 'users'
                          ? editedPolicy.includedUsers || []
                          : kind === 'nhis'
                          ? editedPolicy.includedNHIs || []
                          : editedPolicy.includedResources || []
                      }
                      excluded={
                        kind === 'users'
                          ? excludedUsers
                          : kind === 'nhis'
                          ? excludedNHIs
                          : excludedResources
                      }
                      onIncludedChange={(next) => {
                        if (kind === 'users') setPolicyList('includedUsers', next);
                        if (kind === 'nhis') setPolicyList('includedNHIs', next);
                        if (kind === 'resources') setPolicyList('includedResources', next);
                      }}
                      onExcludedChange={(next) => {
                        if (kind === 'users') setPolicyList('excludedUsers', next);
                        if (kind === 'nhis') setPolicyList('excludedNHIs', next);
                        if (kind === 'resources') setPolicyList('excludedResources', next);
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
            )}

            {/* ─── TAB: Execution History ─────────────────────── */}
            {activeTab === 'timeline' && (
            <div className="w-full max-w-full space-y-5 overflow-hidden">

              {executions.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-border/50 bg-card p-8 text-center shadow-lg shadow-sky-500/5">
                  <Activity className="mx-auto mb-3 h-8 w-8 text-slate-300" />
                  <p className="text-sm font-medium text-slate-500">No executions recorded</p>
                  <p className="mt-1 text-xs text-slate-400">
                    Policy execution history will appear here once the policy has been triggered.
                  </p>
                </div>
              ) : (
                <div className="w-full max-w-full overflow-hidden rounded-2xl border border-border/50 bg-card shadow-lg shadow-sky-500/5">
                  <div className="w-full max-w-full divide-y divide-slate-100 overflow-hidden">
                    {[...executions]
                      .sort((a, b) => {
                        const timeA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
                        const timeB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
                        return timeB - timeA;
                      })
                      .map((exec: any, index: number) => {
                      const resultStr = String(exec.result || '').toLowerCase();
                      const isSuccess = resultStr === 'success' || resultStr === 'completed';
                      const statusColor = isSuccess ? 'text-emerald-600' : 'text-rose-600';
                      const statusBg = isSuccess ? 'bg-emerald-100' : 'bg-rose-100';
                      const statusBorder = isSuccess ? 'border-emerald-200' : 'border-rose-200';

                      const headerTitle = exec.actionTaken 
                        || (exec.steps && exec.steps.length > 0 && exec.steps[0].reason)
                        || `Policy Execution: ${exec.mode || 'ENABLE'}`;

                      return (
                        <div key={exec.id || index} className="w-full max-w-full overflow-hidden pt-4 first:pt-0">
                          <div className="flex min-w-0 max-w-full items-start">
                            {/* Execution Card */}
                            <div className="min-w-0 flex-1 overflow-hidden rounded-2xl border border-border/50 bg-card shadow-lg shadow-sky-500/5 transition-all duration-300 hover:border-primary/30 hover:shadow-[0_4px_16px_hsl(var(--primary)/0.06)]">
                              {/* Header */}
                              <div className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-100 bg-slate-50/80 px-5 py-4">
                                <div className="min-w-0 flex-1">
                                  <h4 className="break-words text-[15px] font-semibold text-slate-800">
                                    {headerTitle}
                                  </h4>
                                  <div className="mt-1 flex min-w-0 flex-wrap items-center gap-4 text-xs text-slate-500">
                                    {exec.timestamp && (
                                      <span className="flex items-center gap-1.5">
                                        <Clock className="h-3.5 w-3.5 text-slate-400" />
                                        {new Date(exec.timestamp).toLocaleString(undefined, {
                                          month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
                                        })} ({formatRelativeTime(exec.timestamp)})
                                      </span>
                                    )}
                                    {exec.targetEntity && exec.targetEntity !== 'Unknown target' && (
                                      <span className="flex items-center gap-1.5">
                                        Target: <span className="break-all font-medium text-slate-700">{exec.targetEntity}</span>
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <Badge variant="outline" className={`px-3 py-1 text-xs uppercase tracking-wider font-bold ${statusBorder} ${statusBg} ${statusColor}`}>
                                  {exec.result}
                                </Badge>
                              </div>

                              {/* Details/Triggered By */}
                              {(exec.details || exec.triggeredBy) && (
                                <div className="border-b border-slate-100 bg-white px-5 py-3">
                                  {exec.details && <p className="text-[15px] text-slate-600 mb-1">{exec.details}</p>}
                                  {exec.triggeredBy && <p className="text-[13px] text-slate-400">Triggered by: {exec.triggeredBy}</p>}
                                </div>
                              )}

                              {/* Steps Timeline */}
                              {exec.steps && exec.steps.length > 0 && (
                                <div className="max-w-full overflow-hidden px-5 py-6">
                                  <div className="relative max-w-full space-y-8 overflow-hidden">
                                    {exec.steps.map((step: any, stepIdx: number) => {
                                      const isStepSuccess = step.output?.exitCode === 0;
                                      const isLastStep = stepIdx === exec.steps.length - 1;
                                      return (
                                        <div key={stepIdx} className="relative flex min-w-0 max-w-full items-start gap-5 pl-10">
                                          {/* Connecting Line */}
                                          {!isLastStep && (
                                            <div className={`absolute left-[11px] top-[26px] -bottom-[30px] w-[2px] ${isStepSuccess ? 'bg-emerald-200' : 'bg-rose-200'}`} />
                                          )}
                                          {/* Step Node */}
                                          <div className={`absolute left-0 top-0.5 flex h-6 w-6 items-center justify-center rounded-full border-2 bg-white text-[10px] font-bold ring-4 ring-white ${
                                            isStepSuccess ? 'border-emerald-500 text-emerald-600' : 'border-rose-500 text-rose-600'
                                          }`}>
                                            {step.step}
                                          </div>
                                          
                                          <div className="min-w-0 max-w-full flex-1 space-y-3 overflow-hidden">
                                            {/* Step Header */}
                                            <div className="flex flex-wrap items-center justify-between gap-2">
                                              <h5 className="min-w-0 break-words text-[13px] font-bold text-slate-800">{step.reason || `Step ${step.step}`}</h5>
                                              <Badge variant="outline" className={`text-[10px] rounded-md ${
                                                isStepSuccess ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-rose-200 bg-rose-50 text-rose-700'
                                              }`}>
                                                {isStepSuccess ? 'Success' : `Failed (Exit ${step.output?.exitCode})`}
                                              </Badge>
                                            </div>

                                            {/* Command Block */}
                                            <div className="max-w-full overflow-hidden rounded-2xl border border-border/50 bg-muted/20 shadow-sm">
                                              <div className="flex items-center gap-1.5 border-b border-slate-200/60 bg-white px-3 py-2">
                                                <div className="h-2 w-2 rounded-full bg-slate-200" />
                                                <div className="h-2 w-2 rounded-full bg-slate-200" />
                                                <div className="h-2 w-2 rounded-full bg-slate-200" />
                                                <span className="ml-2 text-[10px] font-bold tracking-widest text-slate-400 uppercase">Command</span>
                                              </div>
                                              <div className="max-w-full overflow-hidden p-4">
                                                <code className="block max-w-full whitespace-pre-wrap break-all font-mono text-xs leading-relaxed text-slate-700">
                                                  <span className="text-slate-400 select-none mr-3">$</span>
                                                  {step.command}
                                                </code>
                                              </div>
                                            </div>

                                            {/* Output Blocks */}
                                            {step.output?.stdout && step.output.stdout.trim() !== '' && (
                                              <div className="max-w-full overflow-hidden rounded-2xl border border-border/50 bg-muted/20 p-4 shadow-sm">
                                                <span className="mb-2 block text-[11px] font-bold uppercase tracking-widest text-slate-500">Standard Output</span>
                                                <pre className="max-w-full overflow-hidden whitespace-pre-wrap break-all font-mono text-[11px] leading-relaxed text-slate-700">
                                                  {step.output.stdout.trim()}
                                                </pre>
                                              </div>
                                            )}
                                            
                                            {step.output?.stderr && step.output.stderr.trim() !== '' && (
                                              <div className="max-w-full overflow-hidden rounded-2xl border border-rose-100 bg-rose-50/40 p-4 shadow-sm">
                                                <span className="mb-2 block text-[11px] font-bold uppercase tracking-widest text-rose-500">Standard Error</span>
                                                <pre className="max-w-full overflow-hidden whitespace-pre-wrap break-all font-mono text-[11px] leading-relaxed text-rose-700">
                                                  {step.output.stderr.trim()}
                                                </pre>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            )}

            {/* ─── TAB: Policy Data ───────────────────────────────── */}
            {activeTab === 'data' && (
            <div className="space-y-5">

              <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                {/* Policy Enable Data */}
                <section className="rounded-2xl border border-border/50 bg-card p-4 shadow-lg shadow-sky-500/5 hover:border-primary/30 hover:shadow-[0_4px_16px_hsl(var(--primary)/0.06)] transition-all duration-300">
                  <div className="mb-3 flex items-center gap-2">
                    <span className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-emerald-200 bg-emerald-50 text-emerald-600">
                      <ShieldCheck className="h-4 w-4" />
                    </span>
                    <div>
                      <h3 className="text-sm font-semibold text-slate-900">Policy Enable Data</h3>
                      <p className="text-xs text-slate-500">Read-only backend action configuration.</p>
                    </div>
                  </div>
                  <div className="h-[75vh] overflow-auto rounded-2xl border border-border/50 bg-muted/20 p-4">
                    <pre className="whitespace-pre-wrap break-words font-mono text-xs leading-5 text-slate-700">
                      {editedPolicy.policyEnableData
                        ? JSON.stringify(editedPolicy.policyEnableData, null, 2)
                        : '// No policy enable data available'}
                    </pre>
                  </div>
                </section>

                {/* Policy Disable Data */}
                <section className="rounded-2xl border border-border/50 bg-card p-4 shadow-lg shadow-sky-500/5 hover:border-primary/30 hover:shadow-[0_4px_16px_hsl(var(--primary)/0.06)] transition-all duration-300">
                  <div className="mb-3 flex items-center gap-2">
                    <span className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-rose-200 bg-rose-50 text-rose-600">
                      <ShieldOff className="h-4 w-4" />
                    </span>
                    <div>
                      <h3 className="text-sm font-semibold text-slate-900">Policy Disable Data</h3>
                      <p className="text-xs text-slate-500">Read-only rollback/disable configuration.</p>
                    </div>
                  </div>
                  <div className="h-[75vh] overflow-auto rounded-2xl border border-border/50 bg-muted/20 p-4">
                    <pre className="whitespace-pre-wrap break-words font-mono text-xs leading-5 text-slate-700">
                      {editedPolicy.policyDisableData
                        ? JSON.stringify(editedPolicy.policyDisableData, null, 2)
                        : '// No policy disable data available'}
                    </pre>
                  </div>
                </section>
              </div>
            </div>
            )}

          </div>
        </ScrollArea>

        <DialogFooter className="border-t border-border/50 bg-card p-4">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button className="rounded-md px-6" onClick={handleSave} disabled={isSaving}>
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                {isCreateMode ? 'Update Selection' : 'Save Changes'}
                <ChevronRight className="ml-2 h-4 w-4" />
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
